import { useEffect, useState } from 'react';
import { FilledButton, OutlinedTextField, Form, Text, SelectOption, OutlinedSelect, BannerMessage, Divider, Link,} from '../../../common/components';
import { Action, ActionStatus, useAppDispatch, useAppSelector,} from '../../../store';
import { getDepartments } from '../../../store/slices/department';
import { getUserProfiles } from '../../../store/slices/userprofile';
import { getJobTitles } from '../../../store/slices/jobTitle/jobTitle.slice';
import { getPracticeAreas } from '../../../store/slices/practiceArea';
import { useInterpolatedPageStrings, useLayout, usePageStrings,} from '../../../common/hooks';
import { getLocations } from '../../../store/slices/location';
import { VisibilityOption } from '../../../viewModels/configuration/visibilityOption';
import { authenticationService } from '../../../store/slices/authentication';
import { useNavigate } from 'react-router-dom';
import { addSnackbarMessage, SnackbarMessage,} from '../../../store/slices/snackbar';
import { PasswordRule,} from '../../../common/features/password/PasswordRule';
import { useTranslation } from 'react-i18next';

import styles from './createAccount.module.scss';
import { passwordRequirements } from '../../../common/validationRule/validationRule';
import { NotFound } from "../../errors/notFound/NotFound";
import { getConfiguration } from "../../../store/slices/configuration";

export function CreateAccount() {
  const pageStrings = usePageStrings();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [createAccountAction, setCreateAccountAction] = useState(Action.idle());

  const [firstName, setFirstName] = useState('');

  const [lastName, setLastName] = useState('');

  const [userName, setUserName] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordPatternError, setPasswordPatternError] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [formReady, setFormReady] = useState(false);

  const [email, setEmail] = useState('');
  
  const department = useAppSelector((state) =>  state.department);
  const location = useAppSelector((state) =>  state.location);
  const practiceArea = useAppSelector((state) => state.practiceArea);
  const jobTitle = useAppSelector((state) =>  state.jobTitle);
  const userProfile = useAppSelector((state) => state.userProfile);
  const configuration = useAppSelector((state) => state.configuration);



  
    // Configure layout
  useLayout({
    authorizationLayoutConfiguration: "wide",
  });

  // Fetch necessary data on component load
  useEffect(() => {
    if (configuration.status === ActionStatus.succeeded) {
        if(configuration.accountSelfCreation.isEnabled)
        {
          if (location.status === ActionStatus.idle) dispatch(getLocations());
          if (department.status === ActionStatus.idle) dispatch(getDepartments());
          if (userProfile.status === ActionStatus.idle) dispatch(getUserProfiles());
          if (jobTitle.status === ActionStatus.idle) dispatch(getJobTitles());
          if (practiceArea.status === ActionStatus.idle) dispatch(getPracticeAreas());
          if (location.status === ActionStatus.succeeded &&
          department.status === ActionStatus.succeeded &&
          userProfile.status === ActionStatus.succeeded &&
          jobTitle.status === ActionStatus.succeeded &&
          practiceArea.status === ActionStatus.succeeded) {
            setFormReady(true);
          };
        }
      }
}, [configuration, department,jobTitle, userProfile, practiceArea, location,dispatch]);

  // Create SelectOptions from data
  const createSelectData = (data: any, nameKey: string, valueKey: string) =>
    data.map((item: { [key: string]: any }, index: number) => (
      <SelectOption
        key={item[valueKey] || `fallback-${index}`}
        optionName={item[nameKey]}
        optionValue={item[valueKey]}
      />
    ));

  const departmentSelectData = createSelectData(department.data, 'name', 'rowGUID');
  const practiceAreaSelectData = createSelectData(practiceArea.data, 'name', 'rowGUID');
  const userProfileSelectData = createSelectData(userProfile.data, 'name', 'rowGUID');
  const jobTitleSelectData = createSelectData(jobTitle.data, 'name', 'rowGUID');
  const locationSelectData = createSelectData(location.data, 'locationName', 'rowGUID');

  const passwordErrorText:string = useInterpolatedPageStrings(pageStrings.createNewUserPage.errorMessage_passwordIncorrectFormat,[pageStrings.createNewUserPage.text_createAccount_password_label]);
 
  // Visibility helpers
  const isVisible = (config: any) => config !== VisibilityOption.Hidden;
  const isDividerVisible = (configs: any[]) => configs.some(config=>config !== VisibilityOption.Hidden);
  const isRequired = (config: any) => config === VisibilityOption.Required;

  // Form submission handler
  const handleSaveAuthenticationActivated = (formData: any) => {
    if (!passwordRequirements.every((req) => new RegExp(req.validation).test(password))) {
      setPasswordPatternError(true);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(true);
      return;
    }

    authenticationService
      .createAccount({
        department: formData['createaccount_department'],
        emailAddress: formData['createaccount_emailAddress'],
        firstname: formData['createaccount_firstName'],
        lastname: formData['createaccount_lastName'],
        location: formData['createaccount_location'],
        password: formData['createaccount_password'],
        phoneNumber: formData['createaccount_telephone'],
        practiceArea: formData['createaccount_practiceArea'],
        profile: formData['createaccount_profile'],
        title: formData['createaccount_title'],
        username: formData['createaccount_username'],
        userCustomString1:formData['createaccount_UserCustomString1'],
        userCustomString2:formData['createaccount_UserCustomString2'],
        userCustomString3:formData['createaccount_UserCustomString3'],
        userCustomString4:formData['createaccount_UserCustomString4'],
        userCustomString5:formData['createaccount_UserCustomString5'],
        userCustomString6:formData['createaccount_UserCustomString6'],
      
        street1:formData['createaccount_Street1'],
        street2:formData['createaccount_Street2'],
        city:formData['createaccount_City'],
        state:formData['createaccount_State'],
        country:formData['createaccount_Country'],
        zipCode:formData['createaccount_ZipCode'],
      })
      .then((result) => {
        if (result.isSuccess) {
          dispatch(addSnackbarMessage(new SnackbarMessage(result.value, 5)));
          setCreateAccountAction(Action.succeeded());
          navigate('/authentication/signin');
        } else {
          setCreateAccountAction(Action.failed(result.errorMessage));
        }
      });
  };

  // email change handlers
  const handleEmailChange = (e: { target: { value: string } }) => {
    setEmail(e.target.value);  
  };

  // Password change handlers
  const handlePasswordChange = (e: { target: { value: string } }) => {
    console.log('handlePasswordChange');
    checkPasswords();
    setPassword(e.target.value);
    if (passwordPatternError) setPasswordPatternError(false);
  };

  // first name change handlers
  const handleFirstNameChange = (e: { target: { value: string } }) => {
    setFirstName(e.target.value);
  }

// last name change handlers
  const handleLastNameChange = (e: { target: { value: string } }) => {
    setLastName(e.target.value);     
  };

  // user name change handlers
  const handleUserNameChange = (e: { target: { value: string } }) => {
    setUserName(e.target.value);  
  };

  function checkPasswords(){
    const inputConfirmPassword = document.getElementById('createaccount-confirmPassword') as HTMLInputElement;
    const inputPassword = document.getElementById('createaccount-password') as HTMLInputElement;
    console.log('checkPasswords', inputConfirmPassword.value, inputPassword.value);
    if ((((inputConfirmPassword.value !== inputPassword.value) && inputConfirmPassword.value.length>0 )&& inputPassword.value.length>0)){
    console.log('confirmPassword dont match',inputConfirmPassword , inputPassword);

    // Passwords do not match, show mismatch error
    inputConfirmPassword.setCustomValidity(t(pageStrings.createNewUserPage.errorMessage_passwordMismatch));
    setPasswordMatchError(true);
    }
    else
    {
      console.log('checkPasswords Match');
      inputPassword.setCustomValidity('');
      inputConfirmPassword.setCustomValidity('');
      setPasswordMatchError(false);
    }
  }

  function handleConfirmPasswordChange() {
    const inputConfirmPassword = document.getElementById('createaccount-confirmPassword') as HTMLInputElement;
    const inputPassword = document.getElementById('createaccount-password') as HTMLInputElement;
    console.log('Checking Passwords',inputConfirmPassword.value , password)
    
    // Check if passwords match
    if (!inputConfirmPassword.value) {
      console.log('confirmPassword Empty');
      // If confirmPassword is empty, show "Required" error
      inputConfirmPassword.setCustomValidity(t(pageStrings.createNewUserPage.errorMessage_required));
      setPasswordMatchError(true);
    } else if (((inputConfirmPassword.value === inputPassword.value) && inputConfirmPassword.value.length > 0 ) && inputPassword.value.length > 0) {
      console.log('confirmPassword Match');

      // Passwords match, clear error
      inputConfirmPassword.setCustomValidity('');      
      inputPassword.setCustomValidity('');
      setPasswordMatchError(false);
    } else if (((inputConfirmPassword.value !== inputPassword.value) && inputConfirmPassword.value.length > 0 ) && inputPassword.value.length > 0) {
      console.log('confirmPassword dont match',inputConfirmPassword.value , inputPassword.value);

      // Passwords do not match, show mismatch error
      inputConfirmPassword.setCustomValidity(t(pageStrings.createNewUserPage.errorMessage_passwordMismatch));      
      setPasswordMatchError(true);
    }
    else{
      inputConfirmPassword.setCustomValidity('');
      setPasswordMatchError(false);
    }

    // Trigger browser validation
    inputConfirmPassword.reportValidity();

    // Update value state (optional, if needed elsewhere)
    setConfirmPassword(inputConfirmPassword.value);
  }

  if(!configuration.accountSelfCreation.isEnabled)
      {
        console.log('configurationAccountSelfCreation',configuration.accountSelfCreation);
          return (
              <NotFound></NotFound>
          );
      }

  return (
    <div className={styles.CreateAccount}>
    {/* <div className={styles['CreateAccount']}> */}
      <header>
        <h1 className={styles['CreateAccount-heading']}>
          <Text
            fontStyle="headline-small"
            value={pageStrings.createNewUserPage.heading_createAccount_label}
          />
        </h1>
      </header>

      {createAccountAction.status === ActionStatus.failed && createAccountAction.message && (
        <div className={styles['CreateAccount-errorMessage']}>
          <BannerMessage messageType="error" message={createAccountAction.message} />
        </div>
      )}

      <Form onSubmit={handleSaveAuthenticationActivated}>
        <fieldset disabled={!formReady}>
          <div className={styles['CreateAccount-formFields']}>
            <OutlinedTextField
              id="createAccount-firstName"
              name="createaccount_firstName"
              required
              type='text'
              label={pageStrings.createNewUserPage.text_createAccount_firstName_label}
              placeholder={pageStrings.createNewUserPage.text_createAccount_firstName_label}        
              value={firstName}
              onChange={handleFirstNameChange}
              pattern={`^(?=.{1,100}$)([À-ǿa-zA-Z\\s.'\\-]+)$`}              
              userDefinedNote={pageStrings.createNewUserPage.text_createAccount_firstName_userDefinedNote}   
            />

            <OutlinedTextField
              name="createaccount_lastName"
              required
              type='text'
              label={pageStrings.createNewUserPage.text_createAccount_lastName_label}
              placeholder={pageStrings.createNewUserPage.text_createAccount_lastName_label}
              value={lastName}
              onChange={handleLastNameChange}            
              pattern={`^(?=.{1,100}$)([À-ǿa-zA-Z0-9\\s.'-]+)$`}
              userDefinedNote={pageStrings.createNewUserPage.text_createAccount_lastName_userDefinedNote} 
            />

            <OutlinedTextField
              name="createaccount_emailAddress"
              required
              label={pageStrings.createNewUserPage.text_createAccount_emailAddress_label}
              type="text"
              placeholder={pageStrings.createNewUserPage.text_createAccount_emailAddress_label}
              value={email}
              onChange={handleEmailChange}
              pattern={`^(?=.{6,359}$)((?:(?:(?:[À-ǿa-zA-Z0-9][\\-._']?)*)[À-ǿa-zA-Z0-9]+)@{1}(?:(?:(?:[À-ǿa-zA-Z0-9][\\-._']?)*)[À-ǿa-zA-Z0-9]+)[.]{1}[À-ǿa-zA-Z]{2,63})$`}
              userDefinedNote={pageStrings.createNewUserPage.text_createAccount_emailAddress_userDefinedNote} 
            />

            <OutlinedTextField
                name="createaccount_username"
                required
                label={pageStrings.createNewUserPage.text_createAccount_userName_label}
                type="text"
                placeholder={pageStrings.createNewUserPage.text_createAccount_userName_label}
                value={userName}
                onChange={handleUserNameChange}
                pattern={`^(?=.{1,104}$)([À-ǿa-zA-Z0-9\\-._'~@]+)$`}                
                userDefinedNote={pageStrings.createNewUserPage.text_createAccount_loginUserName_userDefinedNote} 
            />


            <Divider />

            <OutlinedTextField
              id="createaccount-password"
              name="createaccount_password"
              required
              label={pageStrings.createNewUserPage.text_createAccount_password_label}
              type="password"
              placeholder={pageStrings.createNewUserPage.text_createAccount_password_label}
              value={password}
              isError={passwordPatternError}
              errorText={passwordPatternError ? passwordErrorText : ''}
              onChange={handlePasswordChange}
              onInput={checkPasswords}
              userDefinedNote={pageStrings.createNewUserPage.text_createAccount_password_userDefinedNote} 
            />

            <ul>
              {passwordRequirements.map((requirement, index) => (
                <li key={index}>
                  <PasswordRule
                    password={password}
                    label={requirement.label}
                    validation={requirement.validation}
                  />
                </li>
              ))}
            </ul>

            <OutlinedTextField
              id="createaccount-confirmPassword"
              name="createaccount_confirmPassword"
              required
              type="password"
              label={pageStrings.createNewUserPage.text_createAccount_confirmPassword_label}
              placeholder={pageStrings.createNewUserPage.text_createAccount_confirmPassword_label}
              isError={passwordMatchError}
              value={confirmPassword}
              errorText={passwordMatchError ? t(pageStrings.createNewUserPage.errorMessage_passwordMismatch) : ''}
              onChange={handleConfirmPasswordChange}              
              onInput={checkPasswords}
              userDefinedNote={pageStrings.createNewUserPage.text_createAccount_passwordConfirm_userDefinedNote} 
            />

            { 
            isDividerVisible([configuration.accountSelfCreation.formConfiguration_Telephone,
              configuration.accountSelfCreation.formConfiguration_Location,
              configuration.accountSelfCreation.formConfiguration_Title,
              configuration.accountSelfCreation.formConfiguration_Department,
              configuration.accountSelfCreation.formConfiguration_PracticeArea,
              configuration.accountSelfCreation.formConfiguration_UserProfile
            ]) &&
              <Divider />     
            }

            {isVisible(configuration.accountSelfCreation.formConfiguration_Telephone) && (
              <>
                <OutlinedTextField
                name="createaccount_telephone"
                label={pageStrings.createNewUserPage.text_createAccount_telephone_label}
                type="tel"
                placeholder={pageStrings.createNewUserPage.text_createAccount_telephone_label}
                required={isRequired(configuration.accountSelfCreation.formConfiguration_Telephone)}
                maxLength={12}
                userDefinedNote={pageStrings.createNewUserPage.text_createAccount_phoneNumber_userDefinedNote}                
                />
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_Location) && (
              <>
                <OutlinedSelect
                  name="createaccount_location"
                  label={pageStrings.createNewUserPage.text_createAccount_location_label}
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Location)}
                  disabled={location.status !== ActionStatus.succeeded}
                  includeBlankOption={true}   
                  
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_location_userDefinedNote} 
                  >
                  {locationSelectData}
                </OutlinedSelect>
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_Title) && (
              <>
                <OutlinedSelect
                  name="createaccount_title"
                  label={pageStrings.createNewUserPage.text_createAccount_title_label}
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Title)}
                  disabled={location.status !== ActionStatus.succeeded}
                  includeBlankOption={true}   
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userTitle_userDefinedNote}                
                >
                  {jobTitleSelectData}
                </OutlinedSelect>
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_Department) && (
              <>
                <OutlinedSelect
                  name="createaccount_department"
                  label={pageStrings.createNewUserPage.text_createAccount_department_label}
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Department)}
                  disabled={location.status !== ActionStatus.succeeded}
                  includeBlankOption={true}
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userDepartment_userDefinedNote} 
                  >
                  {departmentSelectData}
                </OutlinedSelect>
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_PracticeArea) && (
              <>
                <OutlinedSelect
                  name="createaccount_practiceArea"
                  label={pageStrings.createNewUserPage.text_createAccount_practiceArea_label}
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_PracticeArea)}
                  disabled={location.status !== ActionStatus.succeeded}
                  includeBlankOption={true}  
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userPracticeArea_userDefinedNote}             
                  >
                  {practiceAreaSelectData}
                </OutlinedSelect>
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserProfile) && (
              <>
                <OutlinedSelect
                  name="createaccount_profile"
                  label={pageStrings.createNewUserPage.text_createAccount_profile_label}
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserProfile)}
                  disabled={userProfile.status !== ActionStatus.succeeded}
                  includeBlankOption={true}
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userProfile_userDefinedNote}  
                >
                  {userProfileSelectData}
                </OutlinedSelect>
              </>
            )}



            { isDividerVisible([configuration.accountSelfCreation.formConfiguration_Street1,
                          configuration.accountSelfCreation.formConfiguration_Street2,
                          configuration.accountSelfCreation.formConfiguration_City,
                          configuration.accountSelfCreation.formConfiguration_State,
                          configuration.accountSelfCreation.formConfiguration_Country,
                          configuration.accountSelfCreation.formConfiguration_ZipCode
                        ]) &&
              <Divider />
            }

            {isVisible(configuration.accountSelfCreation.formConfiguration_Street1) && (
              <>
                <OutlinedTextField
                  id="createaccount-Street1"
                  name="createaccount_Street1"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Street1)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_street1_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_street1_label}   
                  maxLength={255}   
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressStreet1_userDefinedNote}         
                /> 
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_Street2) && (
              <>
                <OutlinedTextField
                  id="createaccount-Street2"
                  name="createaccount_Street2"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Street2)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_street2_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_street2_label}  
                  maxLength={255}
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressStreet2_userDefinedNote}     
                />
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_City) && (
              <>
                <OutlinedTextField
                  id="createaccount-City"
                  name="createaccount_City"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_City)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_city_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_city_label}   
                  maxLength={255}  
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressCity_userDefinedNote} 
                />
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_State) && (
              <> 
               <OutlinedTextField
                  id="createaccount-State"
                  name="createaccount_State"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_State)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_state_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_state_label}      
                  maxLength={255}    
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressState_userDefinedNote}          
                />
              </>
            )}
            
            {isVisible(configuration.accountSelfCreation.formConfiguration_Country) && (
              <>
                <OutlinedTextField
                  id="createaccount-Country"
                  name="createaccount_Country"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_Country)}
                  type="text"              
                  label={pageStrings.createNewUserPage.text_createAccount_country_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_country_label}                
                  maxLength={255}  
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressCountry_userDefinedNote}          
                />
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_ZipCode) && (
              <>
                <OutlinedTextField
                  id="createaccount-ZipCode"
                  name="createaccount_ZipCode"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_ZipCode)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_zipCode_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_zipCode_label}  
                  maxLength={25}   
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_addressZip_userDefinedNote}                 
                />
              </>
            )}    
          

            
            { isDividerVisible([configuration.accountSelfCreation.formConfiguration_UserCustomString1,
                          configuration.accountSelfCreation.formConfiguration_UserCustomString2,
                          configuration.accountSelfCreation.formConfiguration_UserCustomString3,
                          configuration.accountSelfCreation.formConfiguration_UserCustomString4,
                          configuration.accountSelfCreation.formConfiguration_UserCustomString5,
                          configuration.accountSelfCreation.formConfiguration_UserCustomString6
                        ]) &&
              <Divider />
            }

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString1) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString1"
                  name="createaccount_UserCustomString1"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString1)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString1_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString1_label} 
                  maxLength={256}  
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString1_userDefinedNote}    
                />
              </>
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString2) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString2"
                  name="createaccount_UserCustomString2"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString2)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString2_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString2_label} 
                  maxLength={256}   
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString2_userDefinedNote}     
                />    
              </>       
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString3) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString3"
                  name="createaccount_UserCustomString3"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString3)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString3_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString3_label}
                  maxLength={256}   
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString3_userDefinedNote}             
                />
              </> 
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString4) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString4"
                  name="createaccount_UserCustomString4"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString4)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString4_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString4_label} 
                  maxLength={256}          
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString4_userDefinedNote}      
                />
              </> 
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString5) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString5"
                  name="createaccount_UserCustomString5"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString5)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString5_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString5_label} 
                  maxLength={256} 
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString5_userDefinedNote}            
                />
              </> 
            )}

            {isVisible(configuration.accountSelfCreation.formConfiguration_UserCustomString6) && (
              <>
                <OutlinedTextField
                  id="createaccount-UserCustomString6"
                  name="createaccount_UserCustomString6"
                  required={isRequired(configuration.accountSelfCreation.formConfiguration_UserCustomString6)}
                  type="text"
                  label={pageStrings.createNewUserPage.text_createAccount_userCustomString6_label}
                  placeholder={pageStrings.createNewUserPage.text_createAccount_userCustomString6_label}    
                  maxLength={256}    
                  userDefinedNote={pageStrings.createNewUserPage.text_createAccount_userCustomString6_userDefinedNote}           
                />
              </> 
            )}

</div>
          <div className={styles['CreateAccount-contentButton']}>
            <FilledButton type="submit" value={pageStrings.createNewUserPage.button_create_label} />
          </div>

          <div className={styles['CreateAccount-backToSignIn']}>
            <Link to="/authentication/signin" text={pageStrings.forgotCredentials.button_signIn_label} />
          </div>
        </fieldset>
      </Form>
    </div>
  );
}